<script setup lang="ts">
import { computed } from 'vue'

import CampaignListItem from '@/components/CampaignListItem/CampaignListItem.vue'
import CostDataRow from '@/components/CampaignListItem/CostDataRow.vue'
import type { CreatorAdvertiser } from '@/lib/adapters/creator-advertisers.adapter'
import { BoxComponent, FlexComponent, ImageComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  item: CreatorAdvertiser
}>()

const hasLogo = computed(() => Boolean(props.item.logo.key || props.item.logo.baseUrl || props.item.logo.url))
const hasCommission = computed(() => props.item.trafficSourceCosts.some((cost) => Number(cost.cpc.amount) > 0 || Number(cost.rateCpa) > 0))
const openCampaign = (campaignId: number) => {
  window.open(`/campaigns?campaignId=${campaignId}`, '_blank', 'noopener,noreferrer')
}
</script>

<template>
  <BoxComponent :p="20" class="advertiser-item">
    <FlexComponent align="center" justify="space-between" :gap="24" wrap="wrap">
      <FlexComponent align="center" :gap="16" class="advertiser-item__brand">
        <div class="advertiser-item__logo">
          <ImageComponent
            v-if="hasLogo"
            :image="item.logo"
            :width="72"
            :height="32"
            :alt="item.name"
            object-fit="contain"
            resize-type="fit"
          />
        </div>
        <TypographyComponent variant="title-large">{{ item.name }}</TypographyComponent>
      </FlexComponent>

      <FlexComponent v-if="!item.campaign" direction="column" align="center" :gap="4" class="advertiser-item__commission">
        <TypographyComponent variant="label-medium">Commission</TypographyComponent>
        <CostDataRow
          v-if="hasCommission"
          :currency="item.currency"
          :grouped-traffic-source-costs="item.trafficSourceCosts"
          justify-content="center"
        />
        <TypographyComponent v-else-if="item.revenueText" variant="body-medium">
          {{ item.revenueText }}
        </TypographyComponent>
        <TypographyComponent v-else variant="body-medium">No commission</TypographyComponent>
      </FlexComponent>
    </FlexComponent>

    <CampaignListItem
      v-if="item.campaign"
      :item="item.campaign"
      :is-compact="true"
      :show-creator-metrics="true"
      class="advertiser-item__campaign"
      @on-open="openCampaign"
    />
  </BoxComponent>
</template>

<style scoped lang="scss">
.advertiser-item {
  border: 1px solid var(--secondary-8);
  border-radius: 16px;
  background: var(--inverted);

  &__brand {
    min-width: 0;
  }

  &__logo {
    width: 72px;
    min-height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__commission {
    min-width: 180px;
  }

  &__campaign {
    margin-top: 16px;
  }
}

@media (max-width: 768px) {
  .advertiser-item {
    &__brand,
    &__commission {
      width: 100%;
    }

    &__commission {
      align-items: flex-start;
    }
  }
}
</style>
